import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import FormErrorMessage from "@/features/shared/components/form-error-message";
import { RecoverPasswordDialogContent } from "./recover-password";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { signIn, signInSchema } from "@/lib/client/auth";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";

const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldComponents: {
    Input,
  },
  formComponents: {
    Button,
  },
  fieldContext,
  formContext,
});

export function SignInForm() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isRecoverPasswordDialogOpen, setIsRecoverPasswordDialogOpen] = useState(false);

  const { mutateAsync: signInAsync } = useMutation({
    mutationFn: (value: { email: string; password: string }) => signIn(value),
    onSuccess() {
      queryClient.clear();
      toast.success("登录成功");
      router.invalidate();
      router.navigate({ to: "/" });
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
    },
    validators: {
      onChange: signInSchema,
    },
    async onSubmit({ value }) {
      // 错误已由 mutation 的 onError 提示，这里只需吞掉异常，
      // 避免 form.handleSubmit() 返回的 Promise 变成未处理的 rejection
      try {
        await signInAsync(signInSchema.parse(value));
      } catch {}
    },
  });
  return (
    <Card className="w-full border-border/60 shadow-sm">
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg">登录账号</CardTitle>
        <CardDescription>使用邮箱与密码登录 Cnapex Club</CardDescription>
      </CardHeader>
      <form
        id="sign-in-form"
        className="space-y-5 px-1"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <CardContent>
          <form.AppField
            name="email"
            children={({ Input, state, handleBlur, handleChange }) => (
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="email">
                  邮箱
                </label>
                <Input
                  className="h-11"
                  value={state.value}
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleBlur}
                  id="email"
                  name="email"
                />
                {state.meta.isTouched && <FormErrorMessage errors={state.meta.errors} />}
              </div>
            )}
          />

          <form.AppField
            name="password"
            children={({ Input, state, handleBlur, handleChange }) => (
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="password">
                  密码
                </label>
                <Input
                  className="h-11"
                  value={state.value}
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleBlur}
                  id="password"
                  name="password"
                  type="password"
                />
                {state.meta.isTouched && <FormErrorMessage errors={state.meta.errors} />}
              </div>
            )}
          />
        </CardContent>
      </form>
      <CardFooter className="flex flex-col gap-2 border-border/60 bg-muted/30">
        <form.AppForm>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <form.Button
                form="sign-in-form"
                type="submit"
                className="h-11 w-full"
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting && <Loader2 className="animate-spin" />}
                {isSubmitting ? "登录中..." : "登录"}
              </form.Button>
            )}
          />
        </form.AppForm>

        <Dialog open={isRecoverPasswordDialogOpen} onOpenChange={setIsRecoverPasswordDialogOpen}>
          <DialogTrigger
            nativeButton={true}
            render={
              <Button
                variant="link"
                className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground"
                size="sm"
              >
                忘记密码?
              </Button>
            }
          />
          <RecoverPasswordDialogContent close={() => setIsRecoverPasswordDialogOpen(false)} />
        </Dialog>
      </CardFooter>
    </Card>
  );
}
